
    // IMPORTS
    import { computed, defineComponent, nextTick, onMounted, onUnmounted, PropType, ref, watch } from 'vue'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'
    import { IPictureData, usePictureCache } from '@/store/pictureCache'

    import Loader from '@/components/common/loader.vue'

    import { IMaterialDTO, MaterialListItem } from '@/types/material'
    import { buildLocalization } from '@/util'

    const MAX_PICTURE_SCALING = 5

    export default defineComponent({
        name: 'material-picture',

        components: {
            Loader
        },

        props: {
            materialData: { type: Object as PropType<MaterialListItem | IMaterialDTO>, required: true },
            withZoom: { type: Boolean as PropType<boolean>, required: false, default: () => true },
            withBorder: { type: Boolean as PropType<boolean>, required: false, default: () => true },
            lazyLoad: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            maxHeight: { type: Number as PropType<number | null>, required: false, default: () => null },
            showHints: { type: Boolean as PropType<boolean>, required: false, default: () => true },
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            loaded: () => true
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const notificationStore = useNotificationStore()
            const pictureCache = usePictureCache()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const zoomIsActive = ref(false)
            const zoomPane = ref<HTMLDivElement | null>(null)
            const zoomPictureHolder = ref<HTMLDivElement | null>(null)
            const pictureHolder = ref<HTMLDivElement | null>(null)
            const hasLoaded = ref(false)

            // The data below is only used for "lazy loading"
            let observer: IntersectionObserver | null = null
            const isVisible = ref(false)

            const publicData = { zoomIsActive, zoomPane, zoomPictureHolder, pictureHolder, hasLoaded }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const computedObserver = computed(() => {
                if (props.lazyLoad) {
                    if (observer == null && pictureHolder.value != null) {
                        observer = new IntersectionObserver(onVisibilityChanged)
                        observer.observe(pictureHolder.value)
                    }
                    return observer
                }
                return null
            })

            const allowLoad = computed(() => !props.lazyLoad ||
                (computedObserver.value /* to trigger the creation of observer */ != null &&                 
                    isVisible.value                     
                    && pictureCache.activeFetches < 5 // throttle load on backend
                )
            )

            const pictureBlob = computed(() => props.materialData.documents?.find(blob => blob.documentType == 'RES'))
            const hasPicture = computed(() => pictureBlob.value != null)
            const pictureData = computed<IPictureData>(() => (allowLoad.value || hasLoaded.value)
                ? pictureBlob.value != null
                    ? pictureCache.getImage(pictureBlob.value?.blobName)
                    : {
                        pictureData: null,
                        pictureHasLoaded: true,
                        pictureDimensions: {
                            width: 0,
                            height: 0
                        }
                    }
                : {
                    pictureData: null,
                    pictureHasLoaded: false,
                    pictureDimensions: {
                        width: 0,
                        height: 0
                    }
                }
            )


            const computedData = {
                computedObserver,
                pictureBlob,
                hasPicture,
                pictureData
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function openZoom() {
                if (props.withZoom) {
                    zoomIsActive.value = true
                    nextTick(() => adjustZoom())
                }
            }

            function closeZoom() {
                zoomIsActive.value = false
            }

            function imageAsBackground(): object {
                return {
                    'background-image': `url('${pictureData.value.pictureData}')`
                }
            }

            const publicMethods = { localize, openZoom, closeZoom, imageAsBackground }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function adjustZoom() {
                if (!zoomIsActive.value || zoomPane.value == null || zoomPictureHolder.value == null) {
                    return
                }

                const { width, height } = pictureData.value.pictureDimensions

                const paneDimensions = {
                    width: zoomPane.value.clientWidth,
                    height: zoomPane.value.clientHeight
                }

                const maxScaling = {
                    x: Math.min(paneDimensions.width / width, MAX_PICTURE_SCALING),
                    y: Math.min(paneDimensions.height / height, MAX_PICTURE_SCALING)
                }

                const actualScaling = Math.min(maxScaling.x, maxScaling.y)

                const actualPictureDimensions = {
                    width: width * actualScaling,
                    height: height * actualScaling
                }

                notificationStore.addNotification(
                    NotificationDTO.createForDebug(`zoomFactor used: ${actualScaling.toFixed(2)}`, 'info')
                )

                zoomPictureHolder.value.style.backgroundSize = `${actualPictureDimensions.width}px ${actualPictureDimensions.height}px`
            }

            /** Called by the IntersectionObserver to update visibility during lazy load */
            function onVisibilityChanged(entries: IntersectionObserverEntry[]) {
                setTimeout(() => 
                    entries.forEach(entry => {
                        isVisible.value = entry.isIntersecting
                    }), 100)
            }

            /********************************************************************************************************************
             WATCH
             ********************************************************************************************************************/
            watch(pictureData, (newValue) => {
                if (newValue?.pictureHasLoaded) {
                    hasLoaded.value = true
                    emit('loaded')
                }
            })

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(() => {
                window.addEventListener("resize", adjustZoom)
            })

            onUnmounted(() => {
                window.removeEventListener("resize", adjustZoom)
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
